<script setup lang="ts">
import { absMoney, compareMoney, type Money, negateMoney, subtractMoney } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { AccountFlowSummary } from '../internal/types.ts'

const MAX_ROWS = 6

const props = defineProps<{
  flows: readonly AccountFlowSummary[]
  nameOf: (accountId: string) => string | undefined
}>()

const { t } = useI18n()

interface Row {
  readonly accountId: string
  readonly moneyIn: Money
  readonly moneyOut: Money
  readonly transfers: Money
  readonly net: Money
}

const rows = computed<Row[]>(() =>
  [...props.flows]
    .sort((a, b) => compareMoney(absMoney(b.net), absMoney(a.net)))
    .slice(0, MAX_ROWS)
    .map((flow) => ({
      accountId: flow.accountId,
      moneyIn: flow.inflow,
      moneyOut: negateMoney(flow.outflow),
      transfers: subtractMoney(flow.transfersIn, flow.transfersOut),
      net: flow.net,
    })),
)
</script>

<template>
  <div class="overflow-x-auto" tabindex="0">
    <table v-if="rows.length > 0" class="w-full border-collapse text-left">
      <thead>
        <tr class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          <th scope="col" class="py-2 pr-3 font-normal">{{ t('dashboard.account') }}</th>
          <th scope="col" class="py-2 pr-3 text-right font-normal">{{ t('dashboard.moneyIn') }}</th>
          <th scope="col" class="py-2 pr-3 text-right font-normal">{{ t('dashboard.moneyOut') }}</th>
          <th scope="col" class="py-2 pr-3 text-right font-normal">{{ t('dashboard.transfers') }}</th>
          <th scope="col" class="py-2 text-right font-normal">{{ t('dashboard.netResultShort') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.accountId" class="border-t border-[color:var(--color-border)]">
          <th scope="row" class="max-w-[16rem] truncate py-2 pr-3 font-medium text-[color:var(--color-text)]">
            {{ nameOf(row.accountId) ?? '' }}
          </th>
          <td class="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-[color:var(--color-income)]">
            <MoneyAmount :value="row.moneyIn" sign />
          </td>
          <td class="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-[color:var(--color-expense)]">
            <MoneyAmount :value="row.moneyOut" sign />
          </td>
          <td class="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-[color:var(--color-transfer)]">
            <MoneyAmount :value="row.transfers" sign />
          </td>
          <td
            class="whitespace-nowrap py-2 text-right font-semibold tabular-nums text-[color:var(--color-text)]"
          >
            <MoneyAmount :value="row.net" sign />
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else class="text-[color:var(--color-text-muted)]">{{ t('dashboard.noAccountMovement') }}</p>
  </div>
</template>
